import FilterOutlineIcon from '@eduzz/houston-icons/FilterOutline';
import Button from '@eduzz/houston-ui/Button';
import Grid from '@material-ui/core/Grid';
import { Field, Form as FormikForm, useFormikContext } from 'formik';
import moment from 'moment';
import React, { useRef } from 'react';
import { isInclusivelyBeforeDay } from 'react-dates';
import { useObservable } from 'react-use-observable';
import { DateRangePickerFormField } from 'v2/components/Shared/FormFields/DateRangePicker';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import { SelectProduct } from 'v2/components/Shared/FormFields/SelectProduct';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import { ECustomerSearchTypes } from 'v2/interfaces/reports/cancel';
import { cancelService, initialFilter } from 'v2/services/reports/cancel';
import SubscriptionsService from 'v2/services/subscriptions';

import type { ISelectProductMultipleHandles } from 'v2/components/Shared/FormFields/SelectProduct';
import type { ICancelFilter } from 'v2/interfaces/reports/cancel';
import type { ICancelReason } from 'v2/interfaces/subscriptions';

import { useStyles } from '../styles';

import type { FieldProps } from 'formik';
import type { Moment } from 'moment';

function Form() {
  const classes = useStyles();

  const [isLoading] = useObservable(() => cancelService.isLoading(), []);
  const [cancelReason] = useObservable(() => SubscriptionsService.getSubscriptionCancelReasons(), []);

  const { isValid, isSubmitting, resetForm, dirty, values, setFieldValue } = useFormikContext<ICancelFilter>();
  const selectProductRef = useRef<ISelectProductMultipleHandles>(null);

  const clientTypes = [
    { label: 'E-mail', value: ECustomerSearchTypes.EMAIL },
    { label: 'Nome', value: ECustomerSearchTypes.NAME },
    { label: 'CPF/CNPJ', value: ECustomerSearchTypes.DOCUMENT },
  ];

  const customerStringOptions = {
    [ECustomerSearchTypes.NAME]: {
      label: 'Nome',
      placeholder: 'Insira o nome',
    },
    [ECustomerSearchTypes.EMAIL]: {
      label: 'E-mail',
      placeholder: 'Insira o e-mail',
    },
    [ECustomerSearchTypes.DOCUMENT]: {
      label: 'CPF/CNPJ',
      placeholder: 'Insira o CPF/CNPJ',
    },
  };

  const onChangeClientType = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue('client', '');
    setFieldValue('customer_type', e.target.value);
  };

  const isOutsideRange = (day: Moment) => !isInclusivelyBeforeDay(day, moment());

  const handleClean = () => {
    resetForm({ values: initialFilter });
    selectProductRef.current?.unCheckAll();
  };

  const motivesOptions = (cancelReason || []).map((item: ICancelReason) => ({
    label: item.name,
    value: item.id,
  }));

  const loadingMotivesOptions = cancelReason === null;
  const customerLabel = customerStringOptions[!!values.customer_type ? values.customer_type : ECustomerSearchTypes.EMAIL].label;
  const customerPlaceholder = customerStringOptions[!!values.customer_type ? values.customer_type : ECustomerSearchTypes.EMAIL].placeholder;
  const customerMask = values.customer_type === ECustomerSearchTypes.DOCUMENT ? 'document' : null;

  const today = moment();
  const datePreset = [
    {
      id: 'today',
      text: 'Hoje',
      start: today,
      end: today,
    },
    {
      id: 'week',
      text: 'Últimos 7 dias',
      start: moment().subtract(7, 'days'),
      end: today,
    },
    {
      id: '2weeks',
      text: 'Últimos 30 dias',
      start: moment().subtract(30, 'days'),
      end: today,
    },
    {
      id: 'month',
      text: 'Últimos 60 dias',
      start: moment().subtract(60, 'days'),
      end: today,
    },
  ];

  return (
    <FormikForm>
      <div id='cancel-filter' className={classes.root}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6} xl={2}>
            <Field
              id='date'
              label='Período'
              name='date'
              component={DateRangePickerFormField}
              customPreset={datePreset}
              isOutsideRange={isOutsideRange}
              minDate={moment().subtract(120, 'days')}
            />
          </Grid>
          <Grid item xs={12} sm={6} xl={2}>
            <Grid container>
              <Grid item xs={4}>
                <Field
                  id='customer_type'
                  name='customer_type'
                  label='Cliente'
                  inputProps={{
                    className: classes.leftInput,
                  }}
                  options={clientTypes}
                  onChange={onChangeClientType}
                  component={SelectFormField}
                  fullWidth
                />
              </Grid>
              <Grid item xs={8}>
                <Field
                  id='customer'
                  name='customer'
                  component={TextFormField}
                  label={customerLabel}
                  placeholder={customerPlaceholder}
                  mask={customerMask}
                  inputProps={{
                    className: classes.rightInput,
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6} md={4} xl={2}>
            <Field id='product_id' name='product_id'>
              {(props: FieldProps) => <SelectProduct {...props} hasLabel endpoint='/reports/sales-filter-products' ref={selectProductRef} />}
            </Field>
          </Grid>
          <Grid item xs={12} sm={6} md={4} xl={1}>
            <Field id='id' name='id' label='Contrato' mask='number' component={TextFormField} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6} md={4} xl={1}>
            <Field id='invoice_id' name='invoice_id' label='Fatura' mask='number' component={TextFormField} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6} md={4} xl={2}>
            <Field
              id='reason_id'
              name='reason_id'
              label='Motivo'
              loading={loadingMotivesOptions}
              options={motivesOptions}
              component={SelectFormField}
              multiple
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} xl={1} className={classes.buttonContainer}>
            <Button
              id='clean-filter-button'
              onClick={handleClean}
              variant='text'
              disabled={!dirty}
              loading={isSubmitting || isLoading}
              color='positive'
              fullWidth
            >
              Limpar
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={4} xl={1} className={classes.buttonContainer}>
            <Button
              id='filter-button'
              variant='contained'
              disabled={!isValid || isSubmitting || isLoading}
              type='submit'
              loading={isSubmitting || isLoading}
              color='positive'
              startIcon={<FilterOutlineIcon className={classes.filterIcon} />}
              fullWidth
            >
              Filtrar
            </Button>
          </Grid>
        </Grid>
      </div>
    </FormikForm>
  );
}

export default Form;
